import { InteractionStatus, type AccountInfo } from '@azure/msal-browser';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getTokenRequestScopes, loginRequest } from '../auth/authScopes';
import { fetchAdminSession } from '../services/admin/adminSessionService';

export type UserRole = 'Admin' | 'User';

/**
 * Result of the admin-access check.
 * - `checking`: the backend allowlist lookup is in flight (MSAL mode only).
 * - `granted` / `denied`: the signed-in user is / is not on the admin allowlist.
 */
export type AdminAccessState = 'checking' | 'granted' | 'denied';

export interface AuthUser {
  id: string;
  displayName: string;
  roles: UserRole[];
}

export interface AuthContextValue {
  /** True when Entra client/tenant env vars are set (MSAL is active). */
  azureAuthConfigured: boolean;
  /**
   * True once the initial auth-session resolution has settled (MSAL hydration complete, or
   * always true in stub mode). Guard components should wait for this before treating
   * `isAuthenticated: false` as "confirmed signed out" — it is `false` for the first render(s)
   * even when a returning user's session will resolve to signed-in.
   */
  authReady: boolean;
  isAuthenticated: boolean;
  user: AuthUser | null;
  hasRole: (role: UserRole) => boolean;
  /**
   * Admin authorization for backoffice routes. In MSAL mode this reflects the backend allowlist
   * (`GET /api/admin/session`); in stub/offline mode it is derived synchronously from env config.
   */
  adminAccess: AdminAccessState;
  /**
   * Last acquired ID token JWT (sync read), sent as `Authorization: Bearer`. Keep warm with
   * `acquireAccessToken` (e.g. on mount) before API/OData requests.
   */
  getAccessToken: () => string | undefined;
  /** Refreshes the ID token via MSAL silent flow and updates the Bearer cache. */
  acquireAccessToken: () => Promise<string | undefined>;
  login: () => void;
  logout: () => void;
  /** Stub mode only — no-op in MSAL mode. */
  setStubUser: (user: AuthUser | null) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStubAdminFromEnv(): boolean {
  return process.env.REACT_APP_AUTH_STUB_ADMIN === 'true';
}

function devAdminOverride(): boolean {
  return process.env.REACT_APP_AUTH_DEV_ADMIN === 'true';
}

function rolesFromMsalAccount(account: AccountInfo | null): UserRole[] {
  if (!account?.idTokenClaims) {
    return [];
  }
  const claims = account.idTokenClaims as Record<string, unknown>;
  const raw = claims.roles;
  const out: UserRole[] = [];
  if (Array.isArray(raw)) {
    for (const role of raw) {
      if (role === 'Admin' || role === 'User') {
        out.push(role);
      }
    }
    return out;
  }
  if (typeof raw === 'string' && (raw === 'Admin' || raw === 'User')) {
    return [raw];
  }
  return [];
}

function accountToUser(account: AccountInfo | null): AuthUser | null {
  if (!account) {
    return null;
  }
  return {
    id: account.localAccountId || account.homeAccountId,
    displayName: account.name || account.username || 'Signed-in user',
    roles: rolesFromMsalAccount(account),
  };
}

function AuthMsalProvider({ children }: { children: React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();

  // MsalProvider runs its own initialize()/handleRedirectPromise() cycle on mount and reports
  // `inProgress: Startup` until it settles — during that window `useIsAuthenticated()` reads
  // `false` even for an already signed-in returning user. Latch `authReady` the first time MSAL
  // reports `None` so callers can tell "still hydrating" apart from "confirmed signed out", and
  // never let it regress (later `inProgress` changes are normal token-refresh activity).
  const [authReady, setAuthReady] = useState(inProgress === InteractionStatus.None);
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      setAuthReady(true);
    }
  }, [inProgress]);

  const activeAccount = instance.getActiveAccount() ?? accounts[0] ?? null;
  const accountId = activeAccount?.homeAccountId ?? null;
  // Key the account object on its stable id so MSAL cache refreshes (which hand back a new
  // `accounts` array reference) do not churn the memo and re-create downstream callbacks/effects.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const account = useMemo(() => activeAccount, [accountId]);

  useEffect(() => {
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  const apiTokenRef = useRef<string | undefined>(undefined);

  const acquireAccessToken = useCallback(async (): Promise<string | undefined> => {
    if (!account) {
      apiTokenRef.current = undefined;
      return undefined;
    }
    const scopes = getTokenRequestScopes();
    try {
      const result = await instance.acquireTokenSilent({
        account,
        scopes,
      });
      const bearer = result.idToken;
      apiTokenRef.current = bearer;
      return bearer;
    } catch (error) {
        // Do NOT auto-trigger an interactive redirect here: doing so from effects/data-fetches can
        // cause sign-in redirect loops. Interactive sign-in stays user-initiated via `login()`
        // (AdminLoginPage button / RequireSignedIn). Callers treat `undefined` as "no token".  
        console.debug(
            "Silent token acquisition failed.",
            error
        );
    
        apiTokenRef.current = undefined;
    
        return undefined;
    }
  }, [instance, account]);

  // Latest token getter, read from effects without adding it as a dependency (its identity is
  // stable per account, but referencing via ref guarantees no effect re-runs on refresh churn).
  const acquireRef = useRef(acquireAccessToken);
  useEffect(() => {
    acquireRef.current = acquireAccessToken;
  }, [acquireAccessToken]);

  const sleep = (ms: number) =>
    new Promise(resolve => setTimeout(resolve, ms));

  const waitForAccessToken = async (
      retries = 5,
      initialDelay = 300
  ): Promise<string | undefined> => {

      let delay = initialDelay;

      for (let i = 0; i < retries; i++) {

          const token = await acquireRef.current();

          if (token) {
              return token;
          }

          await sleep(delay);

          // Backoff exponencial
          delay *= 2;
      }

      return undefined;
  };

  const user = useMemo(() => accountToUser(account), [account]);

  const hasRole = useCallback(
    (role: UserRole) => {
      if (devAdminOverride() && role === 'Admin') {
        return true;
      }
      return !!user?.roles.includes(role);
    },
    [user]
  );

  const isAuthenticated = isMsalAuthenticated && !!account;

  const [adminAccess, setAdminAccess] = useState<AdminAccessState>('checking');
  // Tracks the account id already resolved so the backend allowlist check runs at most once per
  // account (prevents the acquire/refresh -> re-render -> re-check loop that could block the account).
  const adminCheckedRef = useRef<string | null>(null);

  useEffect(() => {

    // Don't evaluate admin access off a transient MSAL startup blip — wait until hydration has
    // settled at least once so `isAuthenticated`/`accountId` reflect a real, stable answer.
    if (!authReady) {
        return;
    }

    if (!isAuthenticated || !accountId) {

        adminCheckedRef.current = null;

        setAdminAccess("denied");

        return;
    }

    if (devAdminOverride()) {

        adminCheckedRef.current = accountId;

        setAdminAccess("granted");

        void acquireRef.current();

        return;
    }

    // Ya validado correctamente anteriormente
    if (adminCheckedRef.current === accountId) {
        return;
    }

    let active = true;

    setAdminAccess("checking");

    void (async () => {

        // Esperar hasta que MSAL tenga el token
        const token = await waitForAccessToken();

        if (!active) {
            return;
        }

        // Nunca marcar denied solo por timing — pero tampoco dejar la UI en "checking" para
        // siempre. Si se agotaron los retries acotados de waitForAccessToken (~9s en total) sin
        // conseguir un token, ya no es un blip transitorio de hidratación de MSAL: algo más
        // profundo está roto (sesión revocada, interaction_required persistente, etc.). Latch
        // como 'denied' para que RequireAdmin salga del spinner infinito; el usuario puede volver
        // a intentar con login() interactivo desde ahí.
        if (!token) {

            console.warn(
                "Admin session check gave up: no access token after bounded retries."
            );

            adminCheckedRef.current = accountId;

            setAdminAccess("denied");

            return;
        }

        const session = await fetchAdminSession(acquireRef.current);

        if (!active) {
            return;
        }

        if (session) {

            adminCheckedRef.current = accountId;

            setAdminAccess("granted");

            return;
        }

        // Solo aquí asumimos que es una respuesta definitiva.
        adminCheckedRef.current = accountId;

        setAdminAccess("denied");

    })();

    return () => {
        active = false;
    };

  }, [authReady, isAuthenticated, accountId]);

  const getAccessToken = useCallback(() => apiTokenRef.current, []);

  const login = useCallback(() => {
    // Round-trip the current path through MSAL's `state` param so a sign-in kicked off from e.g.
    // /admin/dashboard lands back there instead of at the bare redirectUri origin.
    const state =
      typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined;
    instance.loginRedirect({ ...loginRequest, state }).catch((error) => {
      console.error('Interactive sign-in failed to start.', error);
    });
  }, [instance]);

  const logout = useCallback(() => {
    apiTokenRef.current = undefined;
    const uri =
      process.env.REACT_APP_AZURE_REDIRECT_URI?.trim() ||
      (typeof window !== 'undefined' ? window.location.origin : undefined);
    void instance.logoutRedirect({
      postLogoutRedirectUri: uri,
    });
  }, [instance]);

  const setStubUser = useCallback(() => {
    /* no-op in MSAL mode */
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      azureAuthConfigured: true,
      authReady,
      isAuthenticated,
      user,
      hasRole,
      adminAccess,
      getAccessToken,
      acquireAccessToken,
      login,
      logout,
      setStubUser,
    }),
    [
      authReady,
      isAuthenticated,
      user,
      hasRole,
      adminAccess,
      getAccessToken,
      acquireAccessToken,
      login,
      logout,
      setStubUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function readStubBearerToken(): string | undefined {
  const t = process.env.REACT_APP_AUTH_STUB_BEARER_TOKEN?.trim();
  return t || undefined;
}

function AuthStubProvider({ children }: { children: React.ReactNode }) {
  const [user, setStubUser] = useState<AuthUser | null>(() => {
    if (process.env.REACT_APP_AUTH_STUB === 'true') {
      return {
        id: 'stub-user',
        displayName: 'Stub user',
        roles: readStubAdminFromEnv() ? ['Admin'] : ['User'],
      };
    }
    return null;
  });

  const stubTokenRef = useRef<string | undefined>(readStubBearerToken());

  const getAccessToken = useCallback(
    () => (user ? stubTokenRef.current : undefined),
    [user]
  );

  const hasRole = useCallback(
    (role: UserRole) => !!user?.roles.includes(role),
    [user]
  );

  const adminAccess: AdminAccessState = user?.roles.includes('Admin') ? 'granted' : 'denied';

  const acquireAccessToken = useCallback(async (): Promise<string | undefined> => {
    if (!user) {
      return undefined;
    }
    stubTokenRef.current = readStubBearerToken();
    return stubTokenRef.current;
  }, [user]);

  const login = useCallback(() => {
    if (process.env.REACT_APP_AUTH_STUB === 'true') {
      stubTokenRef.current = readStubBearerToken();
      setStubUser({
        id: 'stub-user',
        displayName: 'Stub user',
        roles: readStubAdminFromEnv() ? ['Admin'] : ['User'],
      });
      return;
    }
    console.warn(
      'Auth: set REACT_APP_AZURE_CLIENT_ID and REACT_APP_AZURE_TENANT_ID, or REACT_APP_AUTH_STUB=true for offline dev.'
    );
  }, []);

  const logout = useCallback(() => {
    stubTokenRef.current = readStubBearerToken();
    setStubUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      azureAuthConfigured: false,
      authReady: true,
      isAuthenticated: !!user,
      user,
      hasRole,
      adminAccess,
      getAccessToken,
      acquireAccessToken,
      login,
      logout,
      setStubUser,
    }),
    [user, hasRole, adminAccess, getAccessToken, acquireAccessToken, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export interface AuthProviderProps {
  children: React.ReactNode;
  /** When true, children are wrapped by `MsalProvider` in `index.tsx` and this uses MSAL. */
  azureAuthEnabled: boolean;
}

export function AuthProvider({
  children,
  azureAuthEnabled,
}: AuthProviderProps): React.ReactElement {
  if (azureAuthEnabled) {
    return <AuthMsalProvider>{children}</AuthMsalProvider>;
  }
  return <AuthStubProvider>{children}</AuthStubProvider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}